// =============================================================================
// Permission helpers — mirror of backend's services/membership.ts
// =============================================================================
// Used to gate UI elements (buttons, menus). The server is the source of truth
// — these checks just hide UI; bypassing them only means seeing a button that
// returns 403.
// =============================================================================

export type Role = 'owner' | 'admin' | 'moderator' | 'member';

export type Permission =
  | 'manage_server'
  | 'manage_members'
  | 'manage_channels'
  | 'moderate_messages'
  | 'moderate_voice'
  | 'view_channel'
  | 'send_messages'
  | 'connect_voice';

const PERMISSION_BY_ROLE: Record<Role, ReadonlySet<Permission>> = {
  owner: new Set([
    'manage_server',
    'manage_members',
    'manage_channels',
    'moderate_messages',
    'moderate_voice',
    'view_channel',
    'send_messages',
    'connect_voice',
  ]),
  admin: new Set([
    'manage_members',
    'manage_channels',
    'moderate_messages',
    'moderate_voice',
    'view_channel',
    'send_messages',
    'connect_voice',
  ]),
  moderator: new Set([
    'manage_channels',
    'moderate_messages',
    'moderate_voice',
    'view_channel',
    'send_messages',
    'connect_voice',
  ]),
  member: new Set([
    'view_channel',
    'send_messages',
    'connect_voice',
  ]),
};

const ROLE_RANK: Record<Role, number> = {
  owner: 4,
  admin: 3,
  moderator: 2,
  member: 1,
};

export function hasPermission(role: Role | string | null | undefined, permission: Permission): boolean {
  if (!role) return false;
  const set = PERMISSION_BY_ROLE[role as Role];
  return set ? set.has(permission) : false;
}

export function outranks(actor: Role | string | null | undefined, target: Role | string | null | undefined): boolean {
  if (!actor || !target) return false;
  const a = ROLE_RANK[actor as Role];
  const t = ROLE_RANK[target as Role];
  if (a === undefined || t === undefined) return false;
  return a > t;
}

export function roleLabel(role: Role | string | null | undefined): string {
  if (!role) return '';
  return role.charAt(0).toUpperCase() + role.slice(1);
}

// Visual styling per role — for member-list badges. Uses solid backgrounds
// and white text so the badge reads cleanly in both light and dark themes.
// Previously the owner badge used a translucent yellow with near-black text,
// which was unreadable on dark backgrounds.
export function roleBadgeClass(role: Role | string | null | undefined): string {
  switch (role) {
    case 'owner':     return 'bg-amber-500 text-white';
    case 'admin':     return 'bg-sky-600 text-white';
    case 'moderator': return 'bg-emerald-600 text-white';
    default:          return 'bg-zinc-500 text-white';
  }
}
